import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { SVGIcon } from '@progress/kendo-svg-icons';
import clsx from 'clsx';
import React, { createContext, CSSProperties, ReactNode, useCallback, useContext, useState } from 'react';
import { ChevronLeftOutline, CloseOutline } from '../../../../icons/icons';

type Dir = 'l' | 'r';

interface FlyoutContextValue {
    setTitle: (title: React.ReactNode) => void;
    setHeaderActions: (node: ReactNode) => void;
    setSubMenu: (subMenuState: { title: string; onBack: () => void } | null) => void;
    close: () => void;
}

const FlyoutHeaderContext = createContext<FlyoutContextValue | null>(null);

export function useFlyoutHeader() {
    const ctx = useContext(FlyoutHeaderContext);
    if (!ctx) {
        throw new Error('useFlyoutHeader must be used inside Flyout');
    }
    return ctx;
}

interface FlyoutProps {
    id?: string;
    show: boolean;
    dir: Dir;
    title?: ReactNode;
    icon?: SVGIcon;
    onClose?: () => void;
    headerActions?: ReactNode;
    children?: ReactNode;
    style?: CSSProperties;
    className?: string;
}

export function FlyoutTitle(props: { children?: React.ReactNode }) {
    const context = React.useContext(FlyoutHeaderContext);
    React.useLayoutEffect(() => {
        context?.setTitle(props.children ?? null);
    }, [props.children]);
    return <></>;
}

export function FlyoutActions(props: { children?: React.ReactNode }) {
    const context = React.useContext(FlyoutHeaderContext);
    React.useLayoutEffect(() => {
        context?.setHeaderActions(props.children);
    }, [props.children]);
    return <></>;
}

export default function Flyout(props: FlyoutProps) {
    const { dir, onClose } = props;

    const [dynamicActions, setDynamicActions] = useState<ReactNode>(props.headerActions);
    const [dynamicTitle, setDynamicTitle] = React.useState<React.ReactNode | null>(null);
    const [subMenu, setSubMenu] = useState<{ title: string; onBack: () => void } | null>(null);
    const [render, setRender] = React.useState(props.show);
    const [open, setOpen] = React.useState(props.show);

    const close = useCallback(() => {
        onClose?.();
    }, [onClose]);

    React.useEffect(() => {
        if (props.show) {
            setRender(true);
            self.setTimeout(() => {
                setOpen(true);
            }, 100);
        } else {
            setOpen(false);
            self.setTimeout(() => {
                setRender(false);
                setDynamicActions(null);
                setDynamicTitle(null);
                setSubMenu(null);
            }, 150);
        }
    }, [props.show]);

    const contextValue = React.useMemo(
        () => ({
            setTitle: setDynamicTitle,
            setHeaderActions: setDynamicActions,
            setSubMenu: setSubMenu,
            close
        }),
        []
    );

    return (
        render && (
            <FlyoutHeaderContext.Provider value={contextValue}>
                <div
                    id={props.id}
                    className={clsx('pe-auto k-card max-h-100 max-w-100', props.className)}
                    style={{
                        ...(props.style || {}),
                        position: 'relative',
                        left: open ? '0%' : dir === 'r' ? '100%' : '-100%',
                        transition: 'left 150ms linear'
                    }}>
                    <div className="secondary-bg-color d-flex align-items-center justify-content-between gap-2 p-2 border-bottom flyout-header">
                        <div className="d-flex gap-2 flex-grow-1 align-items-center">
                            {subMenu ? (
                                <>
                                    <Button
                                        svgIcon={ChevronLeftOutline}
                                        fillMode="flat"
                                        className="button-small"
                                        onClick={() => {
                                            subMenu.onBack();
                                            setSubMenu(null);
                                        }}
                                    />
                                    <span className="fw-semibold text-truncate" style={{ maxWidth: '300px' }}>
                                        {subMenu.title}
                                    </span>
                                </>
                            ) : (
                                <>
                                    {props.icon && <SvgIcon icon={props.icon} />}
                                    <span className="fw-semibold text-truncate">{dynamicTitle ?? props?.title}</span>
                                </>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            {dynamicActions}
                            <Button title={'Close'} svgIcon={CloseOutline} fillMode="flat" className="button-small" onClick={close} />
                        </div>
                    </div>
                    {props.children}
                </div>
            </FlyoutHeaderContext.Provider>
        )
    );
}
